import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Copy, KeyRound, Wallet, Users, ClipboardList, ScrollText, Settings, BadgePercent } from 'lucide-react';
import { EnterpriseAdminService } from '../../services/enterpriseAdminService';
import { EnterpriseForm } from './Enterprises';

const TABS = [
  { key: 'settings', label: 'Settings', icon: Settings },
  { key: 'rates', label: 'Rates', icon: BadgePercent },
  { key: 'users', label: 'Users', icon: Users },
  { key: 'wallet', label: 'Wallet', icon: Wallet },
  { key: 'api', label: 'API Keys', icon: KeyRound },
  { key: 'complaints', label: 'Complaints', icon: ClipboardList },
  { key: 'logs', label: 'API Logs', icon: ScrollText },
];

function UsersTab({ enterpriseId, users, refetch }) {
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '', isOwner: false });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const createUser = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) {
      setError('Email and password required');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await EnterpriseAdminService.createUser(enterpriseId, form);
      if (!res?.success) {
        setError(res?.error || 'Failed to create user');
        return;
      }
      setForm({ name: '', email: '', password: '', phone: '', isOwner: false });
      await refetch();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const input = 'px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500';

  return (
    <div className="space-y-4">
      <form onSubmit={createUser} className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
        <h3 className="font-bold text-gray-900">Create login (email + password)</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input className={input} placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input className={input} type="email" placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <input className={input} type="text" placeholder="Password * (min 8)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          <input className={input} placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </div>
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={form.isOwner} onChange={(e) => setForm({ ...form, isOwner: e.target.checked })} />
            Owner account
          </label>
          <button type="submit" disabled={busy} className="px-5 py-2 bg-primary-600 text-white rounded-lg text-sm font-bold disabled:opacity-60">
            {busy ? 'Creating…' : '+ Create User'}
          </button>
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
      </form>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {(users || []).length === 0 ? (
          <p className="py-10 text-center text-sm text-gray-500">No portal users yet.</p>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-50 border-b border-gray-200 text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Phone</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map((u) => (
                <tr key={u.id}>
                  <td className="px-4 py-3 font-semibold text-gray-900">{u.name}</td>
                  <td className="px-4 py-3">{u.email}</td>
                  <td className="px-4 py-3">{u.phone || '—'}</td>
                  <td className="px-4 py-3">{u.is_owner ? 'Owner' : 'Member'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-bold ${u.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {u.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={async () => {
                        await EnterpriseAdminService.setUserStatus(u.id, u.status === 'active' ? 'disabled' : 'active');
                        await refetch();
                      }}
                      className="text-xs font-bold text-primary-600 hover:underline"
                    >
                      {u.status === 'active' ? 'Disable' : 'Enable'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function WalletTab({ enterpriseId, pendingTopups, refetch }) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const { data: ledgerData, refetch: refetchLedger } = useQuery({
    queryKey: ['enterpriseLedger', enterpriseId],
    queryFn: () => EnterpriseAdminService.ledger(enterpriseId),
  });

  const adjust = async (sign) => {
    const amt = Number(amount);
    if (!amt || amt <= 0) {
      setError('Enter a valid amount');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await EnterpriseAdminService.walletAdjust(enterpriseId, sign * amt, reason);
      if (!res?.success) {
        setError(res?.error || 'Failed');
        return;
      }
      setAmount('');
      setReason('');
      await Promise.all([refetch(), refetchLedger()]);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const approveTopup = async (topupId, approve) => {
    setBusy(true);
    try {
      await EnterpriseAdminService.topupApprove(topupId, approve);
      await Promise.all([refetch(), refetchLedger()]);
    } finally {
      setBusy(false);
    }
  };

  const bankPending = (pendingTopups || []).filter((t) => t.method !== 'razorpay');
  const razorpayPending = (pendingTopups || []).filter((t) => t.method === 'razorpay');

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-600">Current balance</p>
          <p className="text-3xl font-bold text-gray-900">
            ₹{Number(ledgerData?.balance || 0).toLocaleString('en-IN')}
          </p>
          <div className="mt-3 space-y-2">
            <input
              type="number"
              min="1"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Amount (₹)"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason / note"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
            <div className="flex gap-2">
              <button onClick={() => adjust(1)} disabled={busy} className="flex-1 py-2 bg-green-600 text-white rounded-lg text-sm font-bold disabled:opacity-60">
                + Credit
              </button>
              <button onClick={() => adjust(-1)} disabled={busy} className="flex-1 py-2 bg-red-600 text-white rounded-lg text-sm font-bold disabled:opacity-60">
                − Debit
              </button>
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="font-bold text-gray-900 mb-2">Pending top-up requests</p>
          {(pendingTopups || []).length === 0 ? (
            <p className="text-sm text-gray-500">None pending.</p>
          ) : (
            <ul className="space-y-2">
              {bankPending.map((t) => (
                <li key={t.id} className="flex items-center justify-between gap-2 border border-gray-100 rounded-lg p-3">
                  <div>
                    <p className="font-bold text-gray-900">₹{Number(t.amount).toLocaleString('en-IN')}</p>
                    <p className="text-xs text-gray-500">
                      {t.method} {t.utr_ref ? `· UTR ${t.utr_ref}` : ''} · {new Date(t.created_at).toLocaleString('en-IN')}
                    </p>
                  </div>
                  <div className="flex gap-1.5">
                    <button onClick={() => approveTopup(t.id, true)} disabled={busy} className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-bold">
                      Approve
                    </button>
                    <button onClick={() => approveTopup(t.id, false)} disabled={busy} className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-xs font-bold">
                      Reject
                    </button>
                  </div>
                </li>
              ))}
              {razorpayPending.map((t) => (
                <li key={t.id} className="border border-dashed border-gray-200 rounded-lg p-3">
                  <p className="font-bold text-gray-900">₹{Number(t.amount).toLocaleString('en-IN')}</p>
                  <p className="text-xs text-gray-500">
                    Razorpay · awaiting payment · {new Date(t.created_at).toLocaleString('en-IN')}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <p className="font-bold text-gray-900 px-4 py-3 border-b border-gray-100">Ledger</p>
        {(ledgerData?.ledger || []).length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500">No transactions.</p>
        ) : (
          <div className="max-h-[420px] overflow-y-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500 sticky top-0">
                <tr>
                  <th className="px-4 py-2">When</th>
                  <th className="px-4 py-2">Reason</th>
                  <th className="px-4 py-2 text-right">Delta</th>
                  <th className="px-4 py-2 text-right">Balance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {ledgerData.ledger.map((l) => (
                  <tr key={l.id}>
                    <td className="px-4 py-2 text-xs text-gray-500 whitespace-nowrap">
                      {new Date(l.created_at).toLocaleString('en-IN')}
                    </td>
                    <td className="px-4 py-2">{l.reason}</td>
                    <td className={`px-4 py-2 text-right font-bold ${l.delta >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {l.delta >= 0 ? '+' : ''}₹{Math.abs(l.delta).toLocaleString('en-IN')}
                    </td>
                    <td className="px-4 py-2 text-right text-gray-600">
                      ₹{Number(l.balance_after).toLocaleString('en-IN')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function ApiKeysTab({ enterpriseId, apiKeys, refetch }) {
  const [label, setLabel] = useState('');
  const [newKey, setNewKey] = useState(null);
  const [busy, setBusy] = useState(false);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await EnterpriseAdminService.apiKeyCreate(enterpriseId, label);
      if (res?.success && res.api_key) {
        setNewKey(res.api_key);
        setLabel('');
        await refetch();
      } else {
        alert(res?.error || 'Failed to create key');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      {newKey && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl p-4">
          <p className="text-sm font-bold text-amber-800">Copy this key now — shown only once:</p>
          <div className="mt-2 flex items-center gap-2">
            <code className="flex-1 bg-white rounded-lg px-3 py-2 text-xs font-mono overflow-x-auto">{newKey}</code>
            <button
              onClick={() => navigator.clipboard.writeText(newKey)}
              className="px-3 py-2 bg-amber-600 text-white rounded-lg text-xs font-bold flex items-center gap-1"
            >
              <Copy size={12} /> Copy
            </button>
          </div>
          <button onClick={() => setNewKey(null)} className="mt-2 text-xs font-semibold text-amber-700 hover:underline">
            Dismiss
          </button>
        </div>
      )}

      <form onSubmit={create} className="bg-white rounded-xl border border-gray-200 p-4 flex items-end gap-3">
        <div className="flex-1">
          <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Key label</label>
          <input
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Production"
          />
        </div>
        <button type="submit" disabled={busy} className="px-5 py-2 bg-primary-600 text-white rounded-lg text-sm font-bold disabled:opacity-60">
          + Create Key
        </button>
      </form>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {(apiKeys || []).length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500">No API keys.</p>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-50 border-b border-gray-200 text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-3">Label</th>
                <th className="px-4 py-3">Prefix</th>
                <th className="px-4 py-3">Last used</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {apiKeys.map((k) => (
                <tr key={k.id}>
                  <td className="px-4 py-3 font-semibold text-gray-900">{k.label}</td>
                  <td className="px-4 py-3 font-mono text-xs">{k.key_prefix}…</td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {k.last_used_at ? new Date(k.last_used_at).toLocaleString('en-IN') : 'never'}
                  </td>
                  <td className="px-4 py-3">
                    {k.revoked_at ? (
                      <span className="px-2 py-0.5 rounded bg-red-100 text-red-700 text-xs font-bold">revoked</span>
                    ) : (
                      <span className="px-2 py-0.5 rounded bg-green-100 text-green-700 text-xs font-bold">active</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {!k.revoked_at && (
                      <button
                        onClick={async () => {
                          if (window.confirm('Revoke this key?')) {
                            await EnterpriseAdminService.apiKeyRevoke(k.id);
                            await refetch();
                          }
                        }}
                        className="text-xs font-bold text-red-600 hover:underline"
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function ComplaintsTab({ enterpriseId }) {
  const { data } = useQuery({
    queryKey: ['enterpriseComplaints', enterpriseId],
    queryFn: () => EnterpriseAdminService.complaints(enterpriseId),
  });
  const items = data?.items || [];

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {items.length === 0 ? (
        <p className="py-10 text-center text-sm text-gray-500">No complaints yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-50 border-b border-gray-200 text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-3">Service</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Ref</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Mechanic</th>
                <th className="px-4 py-3 text-right">Rate</th>
                <th className="px-4 py-3 text-right">Charged</th>
                <th className="px-4 py-3">Registered</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((c) => (
                <tr key={c.id}>
                  <td className="px-4 py-3 font-semibold text-gray-900">
                    {c.service_type}
                    {c.source === 'enterprise_api' && (
                      <span className="ml-1 px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 text-[10px] font-bold">API</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {c.customer_name}
                    <p className="text-xs text-gray-500">{c.contact_number}</p>
                  </td>
                  <td className="px-4 py-3 text-xs">{c.enterprise_ref || '—'}</td>
                  <td className="px-4 py-3 capitalize">{c.request_status}</td>
                  <td className="px-4 py-3 text-xs">{c.assigned_mechanic_name || '—'}</td>
                  <td className="px-4 py-3 text-right">
                    {c.agreed_amount ? `₹${Number(c.agreed_amount).toLocaleString('en-IN')}` : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {c.enterprise_charge_status === 'charged'
                      ? `₹${Number(c.enterprise_charged_amount || 0).toLocaleString('en-IN')}`
                      : c.enterprise_charge_status === 'failed'
                        ? <span className="text-red-600 font-bold">failed</span>
                        : '—'}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                    {c.requested_at ? new Date(c.requested_at).toLocaleString('en-IN') : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function LogsTab({ enterpriseId }) {
  const { data: logs = [] } = useQuery({
    queryKey: ['enterpriseApiLogs', enterpriseId],
    queryFn: () => EnterpriseAdminService.apiLogs(enterpriseId),
  });

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {logs.length === 0 ? (
        <p className="py-10 text-center text-sm text-gray-500">No API calls yet.</p>
      ) : (
        <div className="max-h-[560px] overflow-y-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500 sticky top-0">
              <tr>
                <th className="px-4 py-2">When</th>
                <th className="px-4 py-2">Method</th>
                <th className="px-4 py-2">Endpoint</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">IP</th>
                <th className="px-4 py-2">Error</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {logs.map((l) => (
                <tr key={l.id}>
                  <td className="px-4 py-2 text-xs text-gray-500 whitespace-nowrap">
                    {new Date(l.created_at).toLocaleString('en-IN')}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">{l.method}</td>
                  <td className="px-4 py-2 font-mono text-xs">{l.endpoint}</td>
                  <td className="px-4 py-2">
                    <span
                      className={`px-1.5 py-0.5 rounded text-xs font-bold ${
                        l.status_code < 400 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {l.status_code}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-xs text-gray-500">{l.ip || '—'}</td>
                  <td className="px-4 py-2 text-xs text-red-600">{l.error || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const EnterpriseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState('settings');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['adminEnterprise', id],
    queryFn: () => EnterpriseAdminService.get(id),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full spinner"></div>
      </div>
    );
  }

  const ent = data?.enterprise;
  if (!ent) {
    return <p className="py-16 text-center text-gray-500">Enterprise not found.</p>;
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/enterprises')} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50">
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">{ent.name}</h1>
          <p className="text-sm text-gray-500">
            Wallet ₹{Number(data.wallet_balance || 0).toLocaleString('en-IN')} · Fee ₹{Number(ent.per_complaint_fee || 0)}/complaint ·{' '}
            <span className={ent.status === 'active' ? 'text-green-600 font-bold' : 'text-red-600 font-bold'}>{ent.status}</span>
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-px">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-t-lg text-sm font-bold border-b-2 ${
                tab === t.key
                  ? 'border-primary-600 text-primary-600 bg-white'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon size={15} /> {t.label}
              {t.key === 'wallet' && (data.pending_topups || []).length > 0 && (
                <span className="ml-1 px-1.5 rounded-full bg-amber-100 text-amber-700 text-[10px]">
                  {data.pending_topups.length}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {tab === 'settings' && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <EnterpriseForm initial={ent} sections="company" onSaved={() => refetch()} />
        </div>
      )}
      {tab === 'rates' && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-sm text-gray-500 mb-4">
            Per-complaint fee, mechanic payment mode, bargain charges and wallet floor. Enterprise portal users can view these but cannot edit them.
          </p>
          <EnterpriseForm initial={ent} sections="rates" onSaved={() => refetch()} />
        </div>
      )}
      {tab === 'users' && <UsersTab enterpriseId={id} users={data.users} refetch={refetch} />}
      {tab === 'wallet' && <WalletTab enterpriseId={id} pendingTopups={data.pending_topups} refetch={refetch} />}
      {tab === 'api' && <ApiKeysTab enterpriseId={id} apiKeys={data.api_keys} refetch={refetch} />}
      {tab === 'complaints' && <ComplaintsTab enterpriseId={id} />}
      {tab === 'logs' && <LogsTab enterpriseId={id} />}
    </div>
  );
};

export default EnterpriseDetail;
